import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check, Star, Zap, Loader2, ShieldCheck, HelpCircle } from 'lucide-react';
import { subscriptionApi } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { getInitialCustomerInfo, isBillingInfoComplete } from '../utils/subscriptionCustomerInfo';
import BillingInfoModal from './BillingInfoModal';

export default function PricingSection({ language = 'en', compact = false }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingPlanId, setProcessingPlanId] = useState(null);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isYearly, setIsYearly] = useState(false);
  const { currentUser, userData } = useAuth();
  const navigate = useNavigate();
  
  const isRTL = language === 'ar';
  const locale = isRTL ? 'ar-EG' : 'en-US';

  const formatEGP = (value) => {
    const amount = Number(value || 0);
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EGP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await subscriptionApi.getPlans(true);
      const apiPlans = response.plans || [];
      // Override prices to static EGP values
      const pricedPlans = apiPlans.map(plan => {
        if (plan.slug === 'free') return { ...plan, price: 0, currency: 'EGP' };
        if (plan.slug === 'premium') return { ...plan, price: 299, currency: 'EGP' };
        return plan;
      });
      setPlans(pricedPlans);
      setError(null);
    } catch (err) {
      console.error('Error fetching plans:', err);
      setError('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const getPrice = (plan) => {
    if (plan.price === 0) return 0;
    if (isYearly) {
      return Math.round(plan.price * 12 * 0.8); // 20% discount
    }
    return plan.price;
  };

  const getPlanColor = (slug) => {
    const colors = {
      free: {
        gradient: 'from-slate-500 to-slate-600',
        badge: 'bg-slate-100 text-slate-700 border-slate-200/60',
        button: 'bg-slate-800 hover:bg-slate-900 text-white shadow-sm border border-slate-750',
        border: 'border-slate-200/80',
        iconBg: 'bg-slate-50',
        iconColor: 'text-slate-600',
        glow: 'shadow-md hover:shadow-lg'
      },
      premium: {
        gradient: 'from-blue-600 to-indigo-600',
        badge: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
        button: 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg shadow-blue-500/25',
        border: 'border-blue-500/60 shadow-[0_0_20px_rgba(59,130,246,0.12)]',
        iconBg: 'bg-blue-50',
        iconColor: 'text-blue-600',
        glow: 'shadow-2xl shadow-blue-500/10'
      }
    };
    return colors[slug] || colors.free;
  };

  const content = {
    en: {
      title: 'Choose Your Plan',
      subtitle: 'Select the perfect plan for your career journey',
      monthly: '/month',
      yearly: '/year',
      free: 'Free Plan',
      popular: 'Most Popular Choice',
      getStarted: 'Get Started',
      startTrial: 'Start Free Trial',
      currentPlan: 'Current Plan',
      loading: 'Loading plans...',
      error: 'Unable to load plans',
      footer: 'Secure Stripe payments. Cancel anytime.',
      processing: 'Processing...',
      loginRequired: 'Sign in to subscribe',
      subscribe: 'Subscribe Now'
    },
    ar: {
      title: 'اختر خطتك المثالية',
      subtitle: 'اختر الخطة التي تدعم مسيرتك المهنية وتساعدك على النجاح',
      monthly: '/شهرياً',
      yearly: '/سنوياً',
      free: 'الخطة المجانية',
      popular: 'الخيار الأكثر شيوعاً',
      getStarted: 'ابدأ الآن مجاناً',
      startTrial: 'ابدأ التجربة المجانية',
      currentPlan: 'الخطة الحالية',
      loading: 'جاري التحميل المباشر للخطط...',
      error: 'تعذر تحميل خطط الاشتراك',
      footer: 'دفع آمن عبر Stripe. يمكنك إلغاء الاشتراك في أي وقت.',
      processing: 'جاري التحلية...',
      loginRequired: 'تسجيل الدخول للاشتراك في الباقة',
      subscribe: 'اشترك الآن'
    }
  };

  const tr = content[language] || content.en;

  const handleSubscribe = async (plan) => {
    if (plan.price === 0) {
      if (currentUser) {
        navigate('/dashboard');
      } else {
        navigate('/register');
      }
      return;
    }

    if (!currentUser) {
      toast.error(tr.loginRequired);
      navigate('/login', { state: { from: '/#pricing', planId: plan.id } });
      return;
    }

    const customerInfo = getInitialCustomerInfo({ currentUser, userData });

    if (!isBillingInfoComplete(customerInfo)) {
      setSelectedPlan(plan);
      setShowBillingModal(true);
      return;
    }

    await processCheckout(plan, customerInfo);
  };

  const processCheckout = async (plan, customerInfo) => {
    try {
      setProcessingPlanId(plan.id);
      
      const response = await subscriptionApi.createSubscriptionSession(
        plan.id,
        `${window.location.origin}/success`,
        `${window.location.origin}/failed`,
        customerInfo
      );

      if (response.checkout_url) {
        window.location.href = response.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('Error creating subscription session:', err);
      toast.error('Failed to start checkout process. Please try again.');
      setProcessingPlanId(null);
    } finally {
      setShowBillingModal(false);
      setSelectedPlan(null);
    }
  };

  if (loading) {
    return (
      <section className={compact ? "py-8" : "py-24 px-4 sm:px-6 lg:px-8 bg-slate-50"}>
        <div className={compact ? "" : "max-w-7xl mx-auto"}>
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin mx-auto text-blue-600" />
            <p className="mt-4 text-slate-600">{tr.loading}</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className={compact ? "py-8" : "py-24 px-4 sm:px-6 lg:px-8 bg-slate-50"}>
        <div className={compact ? "" : "max-w-7xl mx-auto"}>
          <div className="text-center">
            <p className="text-red-600">{tr.error}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={compact ? "py-4" : "py-24 px-4 sm:px-6 lg:px-8 bg-slate-50 relative overflow-hidden"} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Background ambient lighting */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full blur-[140px] bg-blue-500/5 pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full blur-[140px] bg-indigo-500/5 pointer-events-none" />

      <BillingInfoModal
        isOpen={showBillingModal}
        onClose={() => {
          setShowBillingModal(false);
          setSelectedPlan(null);
        }}
        initialData={getInitialCustomerInfo({ currentUser, userData })}
        isProcessing={processingPlanId !== null}
        onSubmit={(info) => {
          if (selectedPlan) {
            processCheckout(selectedPlan, info);
          }
        }}
      />
      
      <div className={compact ? "" : "max-w-7xl mx-auto relative z-10"}>
        {/* Header - Hide in compact mode */}
        {!compact && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full mb-4 shadow-[0_0_15px_rgba(59,130,246,0.05)]"
            >
              <Zap className="w-4 h-4 text-blue-500 animate-pulse" />
              <span className="text-xs font-bold text-blue-500 tracking-wider">
                {language === 'ar' ? 'باقات مرنة ومناسبة' : 'Flexible & Affordable Plans'}
              </span>
            </motion.div>
            
            <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-4 tracking-tight leading-tight">
              {tr.title}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {tr.subtitle}
            </p>

            {/* Monthly / Yearly Switcher */}
            <div className="flex items-center justify-center gap-4 mt-8">
              <span className={`text-sm font-extrabold transition-colors ${!isYearly ? 'text-blue-600' : 'text-slate-400'}`}>
                {language === 'ar' ? 'شهرياً' : 'Monthly'}
              </span>
              <button
                onClick={() => setIsYearly(!isYearly)}
                className="w-14 h-8 bg-slate-200 hover:bg-slate-350 rounded-full p-1 transition-colors duration-300 focus:outline-none relative"
                aria-label="Toggle billing cycle"
              >
                <motion.div
                  className="w-6 h-6 bg-blue-600 rounded-full shadow-md"
                  animate={{ x: isYearly ? (isRTL ? -24 : 24) : 0 }}
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                />
              </button>
              <span className={`text-sm font-extrabold transition-colors flex items-center gap-1.5 ${isYearly ? 'text-blue-600' : 'text-slate-400'}`}>
                <span>{language === 'ar' ? 'سنوياً' : 'Yearly'}</span>
                <span className="bg-green-500/10 text-green-600 border border-green-500/20 text-[10px] px-2 py-0.5 rounded-full font-black">
                  {language === 'ar' ? 'وفر 20%' : 'Save 20%'}
                </span>
              </span>
            </div>
          </motion.div>
        )}

        {/* Pricing Cards */}
        <div className={`grid gap-8 ${compact ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'md:grid-cols-2 lg:grid-cols-2 max-w-4xl mx-auto items-stretch'}`}>
          {plans.map((plan, index) => {
            const colors = getPlanColor(plan.slug);
            const planName = isRTL ? plan.name_ar : plan.name_en;
            const planDescription = isRTL ? plan.description_ar : plan.description_en;
            const features = plan.features[language] || plan.features.en;
            const computedPrice = getPrice(plan);

            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: compact ? 0 : index * 0.1 }}
                whileHover={{ y: -8, scale: 1.01 }}
                className={`
                  relative bg-white rounded-3xl p-8 border-2 ${colors.border} ${colors.glow}
                  transition-all duration-300 flex flex-col justify-between overflow-hidden
                `}
              >
                {/* Popular Glow Effect */}
                {plan.is_popular && (
                  <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
                )}

                {/* Card Top Label */}
                <div>
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h3 className="font-black text-slate-900 text-2xl mb-1.5">
                        {planName}
                      </h3>
                      <p className="text-slate-500 text-xs font-semibold max-w-[220px] leading-relaxed">
                        {planDescription}
                      </p>
                    </div>

                    {plan.is_popular && (
                      <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${colors.badge} border`}>
                        <Star className="w-3 h-3 fill-current animate-spin" style={{ animationDuration: '3s' }} />
                        {tr.popular}
                      </span>
                    )}
                  </div>

                  {/* Price Block */}
                  <div className="mb-8 p-5 bg-slate-50 rounded-2xl border border-slate-100 flex items-baseline gap-1.5">
                    {plan.price === 0 ? (
                      <span className="font-black text-slate-900 text-4xl">{tr.free}</span>
                    ) : (
                      <>
                        <span className="font-black text-slate-900 text-4xl tracking-tight">
                          {formatEGP(computedPrice)}
                        </span>
                        <span className="text-slate-500 text-sm font-bold">
                          {isYearly ? tr.yearly : tr.monthly}
                        </span>
                      </>
                    )}
                  </div>

                  {/* Features Title */}
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4">
                    {language === 'ar' ? 'ما ستحصل عليه في هذه الباقة:' : 'What\'s included in this plan:'}
                  </div>

                  {/* Features List */}
                  <ul className="space-y-3.5 mb-8">
                    {features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div className={`flex-shrink-0 w-5 h-5 rounded-full ${colors.iconBg} flex items-center justify-center mt-0.5 border border-slate-150`}>
                          <Check className={`w-3 h-3 ${colors.iconColor}`} />
                        </div>
                        <span className="text-slate-700 text-sm font-semibold">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA Action Button */}
                <button
                  onClick={() => handleSubscribe(plan)}
                  disabled={processingPlanId === plan.id}
                  className={`
                    w-full inline-flex items-center justify-center gap-2 px-6 py-4
                    ${colors.button}
                    font-black rounded-2xl text-base
                    transition-all duration-300 transform active:scale-95
                    disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
                  `}
                >
                  {processingPlanId === plan.id ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {tr.processing}
                    </>
                  ) : (
                    <>
                      {plan.price === 0 
                        ? tr.getStarted 
                        : plan.trial_period_days > 0 
                          ? tr.startTrial 
                          : tr.subscribe}
                    </>
                  )}
                </button>
              </motion.div>
            );
          })}
        </div>

        {/* Additional Info Footer */}
        {!compact && (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-col items-center gap-3 mt-16 text-center"
          >
            <div className="flex items-center gap-2 text-slate-500 text-sm font-semibold">
              <ShieldCheck className="w-5 h-5 text-emerald-500" />
              <span>{tr.footer}</span>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
}